/**
 * Enforces end >= start for paired date inputs (YYYY-MM-DD).
 *
 * Optional `prevStart` / `prevEnd`: when the new value would cross the other
 * bound, shift the paired date by the same calendar delta instead of collapsing
 * the range to one day. This keeps native month navigation from hiding all
 * dates when max/min would otherwise block future/past months.
 */

function parseLocalYmd(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

export function onStartDateChange(
  newStart: string,
  end: string,
  prevStart?: string
): { start: string; end: string } {
  if (!newStart) return { start: newStart, end };
  if (!end || end >= newStart) return { start: newStart, end };
  // newStart > end: shift end forward by (newStart - prevStart) when possible
  if (prevStart) {
    const deltaMs =
      parseLocalYmd(newStart).getTime() - parseLocalYmd(prevStart).getTime();
    const nextEnd = new Date(parseLocalYmd(end).getTime() + deltaMs);
    return { start: newStart, end: formatLocalYmd(nextEnd) };
  }
  return { start: newStart, end: newStart };
}

export function onEndDateChange(
  start: string,
  newEnd: string,
  prevEnd?: string
): { start: string; end: string } {
  if (!newEnd) return { start, end: newEnd };
  if (!start || newEnd >= start) return { start, end: newEnd };
  // newEnd < start: shift start backward by (newEnd - prevEnd) when possible
  if (prevEnd) {
    const deltaMs =
      parseLocalYmd(newEnd).getTime() - parseLocalYmd(prevEnd).getTime();
    const nextStart = new Date(parseLocalYmd(start).getTime() + deltaMs);
    return { start: formatLocalYmd(nextStart), end: newEnd };
  }
  return { start: newEnd, end: newEnd };
}

/**
 * After shifting start/end, clamp to MODIS (or other) catalog bounds and
 * ensure start <= end (used by LST/VHI multitemporal pickers).
 */
export function clampDateRangeToAvailability(
  next: { start: string; end: string },
  minAvail: string | null | undefined,
  maxAvail: string | null | undefined
): { start: string; end: string } {
  let { start, end } = next;
  if (minAvail && start < minAvail) start = minAvail;
  if (maxAvail && end > maxAvail) end = maxAvail;
  if (start > end) end = start;
  return { start, end };
}

/** Earliest of two ISO date strings (undefined args ignored). */
export function minDateString(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a <= b ? a : b;
}

/** Latest of two ISO date strings (undefined args ignored). */
export function maxDateString(a: string | undefined, b: string | undefined): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a >= b ? a : b;
}

/** Ensures end_date >= start_date (YYYY-MM-DD). If invalid, aligns both to the end date. */
export function normalizeDateRange(
  start_date: string,
  end_date: string
): { start_date: string; end_date: string } {
  if (!start_date || !end_date) return { start_date, end_date };
  if (end_date >= start_date) return { start_date, end_date };
  const fixed = onEndDateChange(start_date, end_date);
  return { start_date: fixed.start, end_date: fixed.end };
}
